import React from 'react';
import styled from 'styled-components6';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIcon, getIconColor } from '../../../../utils';
import { IconOption } from 'rwl-shared/dist/models/widget';

type ServiceAreaMarkerContentProps = {
    icon: IconOption;
    iconTint: string;
    jobCount: number;
    isCluster?: boolean;
    isPopupOpen?: boolean;
    isHovered?: boolean;
    onClick?: () => void;
};

const getContrastCountColor = (
    icon: IconOption,
    colors: { fill: string; backDrop: string }
) => {
    switch (icon) {
        case IconOption.House:
            return colors.backDrop;
        case IconOption.PointerDark:
            return 'white';
        case IconOption.PointerLight:
        default:
            return 'var(--rwl-text-default)';
    }
};

const getCountColor = (icon: IconOption, iconTint: string) => {
    switch (icon) {
        case IconOption.PointerDark:
            return getIconColor(icon, iconTint).fill;
        case IconOption.PointerLight:
            return getIconColor(icon, iconTint).fill;
        case IconOption.House:
        default:
            return 'var(--rwl-text-default)';
    }
};

export const JobCountSpan = styled.span`
    font-size: 12px;
    font-weight: 500;
`;

export const MarkerContainer = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['colors', 'icon', 'isPopupOpen', 'isActive'].includes(prop),
})<{
    colors: { fill: string; backDrop: string };
    icon: IconOption;
    isPopupOpen?: boolean;
    isActive?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ colors }) => colors.backDrop};
    border-radius: var(--rwl-radius-md);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    padding: calc(var(--rwl-spacing-xs) * 0.5) calc(var(--rwl-spacing-xs) + 1px);
    height: 17px;
    min-width: 38px;
    cursor: pointer;
    --icon-fill: ${({ colors }) => colors.fill};
    --icon-backdrop: ${({ colors }) => colors.backDrop};
    > * {
        cursor: pointer;
    }

    > span {
        color: ${({ icon, colors }) => getCountColor(icon, colors.fill)};
        margin-left: var(--rwl-spacing-xs);
    }

    svg {
        color: var(--icon-fill);
    }

    ${({ isPopupOpen, isActive, colors, icon }) =>
        isPopupOpen || isActive
            ? `
        --icon-fill: ${colors.backDrop};
        --icon-backdrop: ${colors.fill};
        background: ${colors.fill};
        > span {
            color: ${getContrastCountColor(icon, colors)};
        }
    `
            : ''}

    &:hover {
        --icon-fill: ${({ colors }) => colors.backDrop};
        --icon-backdrop: ${({ colors }) => colors.fill};
        background: ${({ colors }) => colors.fill};
        > span {
            color: ${({ colors, icon }) => getContrastCountColor(icon, colors)};
        }
    }
`;

const BaseContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const ClusterBackdrop = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'colors', // Prevents console warnings for invalid props
})<{ colors: { backDrop: string } }>`
    position: absolute;
    top: 2px;
    left: 2px;
    z-index: -1;
    background: 'white';
    border-radius: var(--rwl-radius-md);
    width: 100%;
    height: 100%;
    box-shadow: 0 0px 5px rgba(0, 0, 0, 0.12);
    background: ${({ colors }) => colors.backDrop};
`;

const buildIcon = (icon: IconOption, isCluster: boolean) => {
    return isCluster ? (
        <span className="fa-layers fa-fw">
            <FontAwesomeIcon
                icon={getIcon(icon)}
                className="cluster-icon-back"
                size="1x"
                fontSize={12}
                transform="right-3 up-3"
            />
            <FontAwesomeIcon
                // Using this as a 'stroke' since the stroke attribute does not seem
                // to actually apply anything when used with FontAwesomeIcon
                icon={getIcon(icon)}
                className="cluster-icon-stroke"
                size="1x"
                fontSize={12}
                transform="grow-3"
                style={{ color: 'var(--icon-backdrop)' }}
            />
            <FontAwesomeIcon
                icon={getIcon(icon)}
                className="cluster-icon-front"
                size="1x"
                fontSize={12}
            />
        </span>
    ) : (
        <FontAwesomeIcon icon={getIcon(icon)} fontSize={12} size="1x" />
    );
};

const ServiceAreaMarkerContent: React.FC<ServiceAreaMarkerContentProps> = ({
    icon,
    iconTint,
    jobCount,
    isCluster = false,
    isPopupOpen,
    isHovered,
    onClick,
}) => (
    <BaseContainer>
        {isCluster && <ClusterBackdrop colors={getIconColor(icon, iconTint)} />}
        <MarkerContainer
            className="serviceAreaMarker"
            icon={icon}
            colors={getIconColor(icon, iconTint)}
            isPopupOpen={isPopupOpen}
            isActive={isHovered}
            onClick={onClick}
        >
            {buildIcon(icon, isCluster)}
            <JobCountSpan>{jobCount || 0}</JobCountSpan>
        </MarkerContainer>
    </BaseContainer>
);

export default ServiceAreaMarkerContent;
